/**
 * Streaming tar reader — V105-3-1 (repo / IaC scan of a GitHub tarball).
 *
 * Pure: feed it uncompressed tar bytes chunk by chunk; it keeps only the regular
 * files `accept(path, size)` wants (up to the limits) and counts the rest. Handles
 * ustar prefixes, GNU long names (`L`) and pax headers (`x` path, `g` comment — GitHub
 * puts the commit sha in the global header's `comment`). Symlinks and other entry
 * types are never read. Paths with `..` segments or a leading `/` are refused.
 */
import { Gunzip } from 'fflate';
const BLOCK = 512;
const dec = new TextDecoder();
function cstr(b, off, len) {
    let end = off;
    while (end < off + len && b[end] !== 0)
        end++;
    return dec.decode(b.subarray(off, end));
}
function octal(b, off, len) {
    const s = cstr(b, off, len).trim();
    if (!s)
        return 0;
    // Base-256 (GNU) for very large sizes: refuse rather than misread.
    if ((b[off] & 0x80) !== 0)
        return -1;
    const n = parseInt(s, 8);
    return Number.isFinite(n) && n >= 0 ? n : -1;
}
function paxRecords(bytes) {
    const out = {};
    const s = dec.decode(bytes);
    let i = 0;
    while (i < s.length) {
        const sp = s.indexOf(' ', i);
        if (sp < 0)
            break;
        const len = parseInt(s.slice(i, sp), 10);
        if (!Number.isFinite(len) || len <= 0)
            break;
        const rec = s.slice(sp + 1, i + len - 1);
        const eq = rec.indexOf('=');
        if (eq > 0)
            out[rec.slice(0, eq)] = rec.slice(eq + 1);
        i += len;
    }
    return out;
}
export function safeTarPath(p) {
    const n = p.replace(/\\/g, '/');
    if (!n || n.startsWith('/') || n.split('/').some((seg) => seg === '..'))
        return null;
    return n;
}
/** A push parser. `push(chunk)` per decompressed chunk, then `end()`. */
export function createTarReader(accept, limits) {
    let buf = new Uint8Array(0);
    const files = [];
    let entries = 0;
    let overLimit = 0;
    let total = 0;
    let globalComment = null;
    let ended = false;
    let atBoundary = true;
    // Current entry
    let remaining = 0;
    let padding = 0;
    let keep = null;
    let meta = null;
    let nextPath = null;
    let zeroBlocks = 0;
    const append = (chunk) => {
        if (!buf.length) {
            buf = chunk;
            return;
        }
        const n = new Uint8Array(buf.length + chunk.length);
        n.set(buf);
        n.set(chunk, buf.length);
        buf = n;
    };
    const finishEntry = () => {
        if (keep) {
            const bytes = new Uint8Array(keep.size);
            let o = 0;
            for (const p of keep.parts) {
                bytes.set(p, o);
                o += p.length;
            }
            files.push({ path: keep.path, bytes });
            keep = null;
        }
        if (meta) {
            const all = new Uint8Array(meta.parts.reduce((s, p) => s + p.length, 0));
            let o = 0;
            for (const p of meta.parts) {
                all.set(p, o);
                o += p.length;
            }
            if (meta.type === 'L')
                nextPath = cstr(all, 0, all.length);
            else {
                const rec = paxRecords(all);
                if (meta.type === 'x' && rec.path)
                    nextPath = rec.path;
                if (meta.type === 'g' && rec.comment)
                    globalComment = rec.comment.trim();
            }
            meta = null;
        }
    };
    const step = () => {
        let pos = 0;
        for (;;) {
            if (ended)
                break;
            if (remaining > 0) {
                const take = Math.min(remaining, buf.length - pos);
                if (take <= 0)
                    break;
                const slice = buf.subarray(pos, pos + take);
                if (keep)
                    keep.parts.push(slice.slice());
                if (meta)
                    meta.parts.push(slice.slice());
                remaining -= take;
                pos += take;
                if (remaining > 0)
                    break;
            }
            if (padding > 0) {
                const take = Math.min(padding, buf.length - pos);
                padding -= take;
                pos += take;
                if (padding > 0)
                    break;
            }
            if (!atBoundary) {
                finishEntry();
                atBoundary = true;
            }
            if (buf.length - pos < BLOCK)
                break;
            const h = buf.subarray(pos, pos + BLOCK);
            pos += BLOCK;
            if (h.every((x) => x === 0)) {
                zeroBlocks++;
                if (zeroBlocks >= 2)
                    ended = true;
                continue;
            }
            zeroBlocks = 0;
            const size = octal(h, 124, 12);
            if (size < 0) {
                ended = true;
                break;
            }
            const type = String.fromCharCode(h[156] || 48);
            const magic = cstr(h, 257, 6);
            const prefix = magic.startsWith('ustar') ? cstr(h, 345, 155) : '';
            const name = nextPath ?? (prefix ? `${prefix}/${cstr(h, 0, 100)}` : cstr(h, 0, 100));
            remaining = size;
            padding = size % BLOCK ? BLOCK - (size % BLOCK) : 0;
            atBoundary = size === 0 && padding === 0;
            if (type === 'x' || type === 'g' || type === 'L') {
                meta = { type: type, parts: [] };
                if (size === 0) {
                    finishEntry();
                    atBoundary = true;
                }
                continue;
            }
            nextPath = null;
            if (type !== '0' && type !== '\0' && type !== '7')
                continue; // dirs, links, devices: skipped
            entries++;
            const path = safeTarPath(name);
            if (!path || !accept(path, size))
                continue;
            if (size > limits.maxFileBytes || files.length >= limits.maxFiles || total + size > limits.maxTotalBytes) {
                overLimit++;
                continue;
            }
            total += size;
            keep = { path, parts: [], size };
            if (size === 0) {
                finishEntry();
                atBoundary = true;
            }
        }
        buf = pos >= buf.length ? new Uint8Array(0) : buf.slice(pos);
    };
    return {
        push(chunk) { if (!ended) {
            append(chunk);
            step();
        } },
        end() {
            step();
            return { files, entries, over_limit: overLimit, global_comment: globalComment, complete: ended || (atBoundary && remaining === 0 && buf.length === 0) };
        },
    };
}
/**
 * A gzip-compressed tar (a GitHub tarball): push compressed chunks, get the same summary.
 * Decompression streams through fflate; only the accepted files are buffered.
 */
export function createTarGzReader(accept, limits) {
    const tar = createTarReader(accept, limits);
    let failed = null;
    const gz = new Gunzip((chunk) => { tar.push(chunk); });
    return {
        push(chunk) {
            if (failed)
                return;
            // Defensive copy: the input is never a view a stream may reuse after this push.
            try {
                gz.push(chunk.slice(), false);
            }
            catch (e) {
                failed = e instanceof Error ? e.message : 'gzip error';
            }
        },
        end() {
            if (!failed) {
                try {
                    gz.push(new Uint8Array(0), true);
                }
                catch (e) {
                    failed = e instanceof Error ? e.message : 'gzip error';
                }
            }
            const s = tar.end();
            return { ...s, complete: s.complete && !failed, error: failed };
        },
    };
}
/**
 * 2026-10-12: read a gzip tar from a byte stream. The runtime's native `DecompressionStream('gzip')` (Workers, Node
 * 18+, browsers) decompresses. On staging the fflate path exceeded the Workflow step's 30 s CPU limit on this
 * repository's 137 MB GitHub archive (cpuTime 32 000 ms; no progress or heartbeat after the first entries). In local
 * workerd the native path read the same archive in 0.87 s against fflate's 3.8 s, and it awaits between chunks, so
 * progress and heartbeats keep running. Without it, the fflate reader above. `onChunk` runs after each
 * decompressed chunk (progress).
 */
export async function readTarGzStream(body, accept, limits, onChunk) {
    const Native = globalThis.DecompressionStream;
    if (!Native) {
        const gz = createTarGzReader(accept, limits);
        const r = body.getReader();
        for (;;) {
            const { done, value } = await r.read();
            if (done)
                break;
            if (value)
                gz.push(value);
            await onChunk?.();
        }
        return gz.end();
    }
    const tar = createTarReader(accept, limits);
    let error = null;
    const r = body.pipeThrough(new Native('gzip')).getReader();
    try {
        for (;;) {
            const { done, value } = await r.read();
            if (done)
                break;
            if (value)
                tar.push(value);
            await onChunk?.();
        }
    }
    catch (e) {
        error = e instanceof Error && e.message ? e.message : 'gzip error';
    }
    const s = tar.end();
    return { ...s, complete: s.complete && !error, error };
}
//# sourceMappingURL=tar-stream.js.map